import axios from "axios";
import jwtDecode from "jwt-decode";
import Cookies from "js-cookie";
import { normalizeDagCatalog } from "./dagCatalog";
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const DataContext = createContext();

// 创建一个简单的缓存管理器
const cache = {
  data: {},
  timestamps: {},
  TTL: 5 * 60 * 1000, // 5分钟缓存时间

  set(key, value) {
    this.data[key] = value;
    this.timestamps[key] = Date.now();
  },

  get(key) {
    const timestamp = this.timestamps[key];
    if (!timestamp || Date.now() - timestamp > this.TTL) {
      delete this.data[key];
      delete this.timestamps[key];
      return null;
    }
    return this.data[key];
  },

  clear(key) {
    if (key) {
      delete this.data[key];
      delete this.timestamps[key];
    } else {
      this.data = {};
      this.timestamps = {};
    }
  }
};

export function AppProvidider({ children }) {
  const [authReady, setAuthReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userToken, setUserToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [role, setRole] = useState(null);
  const [organization, setOrganization] = useState(null);

  const updateUserInfo = useCallback(() => {
    const userToken = Cookies.get("user_token");
    const userData = userToken ? jwtDecode(userToken) : null;
    const role = userData ? userData.role : null;
    const isLoggedIn = userData ? true : false;
    const organization = userData ? userData.organization : null;
    setUserToken(userToken);
    setUserData(userData);
    setIsLoggedIn(isLoggedIn);
    setRole(role);
    setOrganization(organization);
    setAuthReady(true);
  }, []);

  useEffect(() => {
    updateUserInfo();
  }, [updateUserInfo]);

  const contextValue = useMemo(() => ({
    authReady,
    isLoggedIn,
    setIsLoggedIn,
    updateUserInfo,
    userToken,
    userData,
    role,
    organization,
  }), [authReady, isLoggedIn, updateUserInfo, userToken, userData, role, organization]);

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
}

export function AcquisitionDataProvider({ children }) {
  const [selectedSource, setSelectedSource] = useState("");
  const [datasetNames, setDatasetNames] = useState([]);
  const [zooDatasetNames, setZooDatasetNames] = useState([]);
  const [folderList, setFolderList] = useState([]);
  const [tableList, setTableList] = useState([]);
  const [availSpace, setAvailSpace] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [CopyPathPage, setCopyPathPage] = useState({
    CopyPathPage: "",
  });
  const [folderType, setFolderType] = useState("image");

  const fetchList = useCallback(async (forceRefresh = false) => {
    // 检查缓存
    const cacheKey = 'acquisitionData';
    const cachedData = !forceRefresh ? cache.get(cacheKey) : null;

    if (cachedData) {
      setDatasetNames(cachedData.datasetNames);
      setZooDatasetNames(cachedData.zooDatasetNames);
      setFolderList(cachedData.folderList);
      setTableList(cachedData.tableList);
      setAvailSpace(cachedData.availSpace);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userToken = Cookies.get("user_token");
      const [datasetNames, zooDatasetNames, folderList, tableList, availSpace] =
        await Promise.all([
          axios.post("/datasets/list?names_only=true", { requesterToken: userToken }),
          axios.get("/datasets/zoo/list"),
          axios.get("/file/folder/list", {
            params: userToken ? { requesterToken: userToken } : {},
          }),
          axios.get("/ETL/table/list"),
          axios.get("/available/space"),
        ]);

      const data = {
        datasetNames: datasetNames.data,
        zooDatasetNames: zooDatasetNames.data,
        folderList: folderList.data.concat(),
        tableList: tableList.data.concat(),
        availSpace: availSpace.data
      };

      // 存入缓存
      cache.set(cacheKey, data);

      setDatasetNames(data.datasetNames);
      setZooDatasetNames(data.zooDatasetNames);
      setFolderList(data.folderList);
      setTableList(data.tableList);
      setAvailSpace(data.availSpace);
    } catch (error) {
      const errorMsg = error.response?.data || error.message || "Failed to fetch data";
      setError(errorMsg);
      console.error("Error fetching acquisition data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const contextValue = useMemo(() => ({
    selectedSource,
    setSelectedSource,
    fetchList,
    datasetNames,
    zooDatasetNames,
    folderList,
    tableList,
    availSpace,
    CopyPathPage,
    setCopyPathPage,
    folderType,
    setFolderType,
    loading,
    error,
  }), [
    selectedSource,
    fetchList,
    datasetNames,
    zooDatasetNames,
    folderList,
    tableList,
    availSpace,
    CopyPathPage,
    folderType,
    loading,
    error,
  ]);

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
}

export function PreLabelProvider({ children }) {
  const [ZooModelNames, setZooModelNames] = useState([]);
  const [kubeflowModelNames, setKubeflowModelNames] = useState([]);
  const [autodagNames, setAutodagNames] = useState([]);
  const [autodagItems, setAutodagItems] = useState([]);
  const [dagScanInfo, setDagScanInfo] = useState(null);
  const [dagLoading, setDagLoading] = useState(false);
  const [dagError, setDagError] = useState(null);
  const [huggingfaceModels, setHuggingfaceModels] = useState([]);
  const [HqSamModels, setHqSamModels] = useState([]);
  const [ultralyticsModels, setUltralyticsModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchModelsList = useCallback(async (forceRefresh = false) => {
    // 检查缓存
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const token = Cookies.get("user_token") || userData.token;
    const cacheKey = `modelsData:${token || "anonymous"}`;
    const cachedData = !forceRefresh ? cache.get(cacheKey) : null;

    if (cachedData) {
      setZooModelNames(cachedData.ZooModelNames);
      setKubeflowModelNames(cachedData.kubeflowModelNames);
      setAutodagNames(cachedData.autodagNames);
      setAutodagItems(cachedData.autodagItems);
      setDagScanInfo(cachedData.dagScanInfo);
      setHuggingfaceModels(cachedData.huggingfaceModels);
      setHqSamModels(cachedData.HqSamModels);
      setUltralyticsModels(cachedData.ultralyticsModels);
      return;
    }

    setLoading(true);
    setDagLoading(true);
    setDagError(null);
    setError(null);

    try {
      // 登录只写了 Cookies.user_token，未写 localStorage userData 时会导致 autodags 不带 token，
      // 后端无法解析 organization，私有 DAG 不会出现在列表里

      const [
        zooModelNames,
        kubeflowModelNames,
        autodagNames,
        huggingfaceModels,
        HqSamModels,
        ultralyticsModels,
      ] = await Promise.all([
        axios.get("/models/zoo/list"),
        axios.get("/models/kubeflow/list"),
        axios.get("/models/kubeflow/autodags", {
          params: { details: "true", ...(token ? { requesterToken: token } : {}) }
        }),
        axios.get("/models/huggingface/models/list"),
        axios.get("/models/hq_sam/models/list"),
        axios.get("/models/ultralytics/models/list"),
      ]);

      const data = {
        ZooModelNames: zooModelNames.data,
        kubeflowModelNames: kubeflowModelNames.data,
        ...normalizeDagCatalog(autodagNames.data),
        huggingfaceModels: huggingfaceModels.data,
        HqSamModels: HqSamModels.data,
        ultralyticsModels: ultralyticsModels.data
      };

      // 存入缓存
      cache.set(cacheKey, data);

      setZooModelNames(data.ZooModelNames);
      setKubeflowModelNames(data.kubeflowModelNames);
      setAutodagNames(data.autodagNames);
      setAutodagItems(data.autodagItems);
      setDagScanInfo(data.dagScanInfo);
      setHuggingfaceModels(data.huggingfaceModels);
      setHqSamModels(data.HqSamModels);
      setUltralyticsModels(data.ultralyticsModels);
    } catch (error) {
      const errorMsg = error.response?.data || error.message || "Failed to fetch models";
      setError(errorMsg);
      setDagError(errorMsg);
      console.error("Error fetching models:", error);
    } finally {
      setLoading(false);
      setDagLoading(false);
    }
  }, []);

  const refreshDags = useCallback(async () => {
    setDagLoading(true);
    setDagError(null);
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const token = Cookies.get("user_token") || userData.token;
      const params = { details: "true", ...(token ? { requesterToken: token } : {}) };
      await axios.post("/models/docker/autodags/reload", null, { params });
      const response = await axios.get("/models/kubeflow/autodags", { params });
      const catalog = normalizeDagCatalog(response.data);
      setAutodagNames(catalog.autodagNames);
      setAutodagItems(catalog.autodagItems);
      setDagScanInfo(catalog.dagScanInfo);
      const cacheKey = `modelsData:${token || "anonymous"}`;
      const cached = cache.get(cacheKey);
      if (cached) cache.set(cacheKey, { ...cached, ...catalog });
    } catch (error) {
      setDagError(error.response?.data?.message || error.response?.data || error.message);
    } finally {
      setDagLoading(false);
    }
  }, []);

  const contextValue = useMemo(() => ({
    ZooModelNames,
    kubeflowModelNames,
    autodagNames,
    autodagItems,
    dagScanInfo,
    dagLoading,
    dagError,
    refreshDags,
    huggingfaceModels,
    HqSamModels,
    ultralyticsModels,
    fetchModelsList,
    loading,
    error,
  }), [
    ZooModelNames,
    kubeflowModelNames,
    autodagNames,
    autodagItems,
    dagScanInfo,
    dagLoading,
    dagError,
    refreshDags,
    huggingfaceModels,
    HqSamModels,
    ultralyticsModels,
    fetchModelsList,
    loading,
    error,
  ]);

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
}

export function CVATProvider({ children }) {
  const [organizations, setOrganizations] = useState([]);
  const [allprojects, setAllprojects] = useState([]);
  const [loading, setLoading] = useState(false);

  const listOrganizations = useCallback(async () => {
    const cacheKey = 'organizations';
    const cachedData = cache.get(cacheKey);

    if (cachedData) {
      setOrganizations(cachedData);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`/organizations/list`);
      const newOrganizations = response.data;
      cache.set(cacheKey, newOrganizations);
      setOrganizations(newOrganizations.concat());
    } catch (error) {
      const errorMsg = error.response?.data || error.message;
      console.error("Error fetching organizations:", errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const listAllprojects = useCallback(async () => {
    const cacheKey = 'allprojects';
    const cachedData = cache.get(cacheKey);

    if (cachedData) {
      setAllprojects(cachedData);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`/projects/list`);
      const fetchedAllprojects = response.data;
      cache.set(cacheKey, fetchedAllprojects);
      setAllprojects(fetchedAllprojects.concat());
    } catch (error) {
      const errorMsg = error.response?.data || error.message;
      console.error("Error fetching projects:", errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    listOrganizations();
    listAllprojects();
  }, [listOrganizations, listAllprojects]);

  const contextValue = useMemo(() => ({
    organizations,
    listOrganizations,
    allprojects,
    listAllprojects,
    loading,
  }), [organizations, listOrganizations, allprojects, listAllprojects, loading]);

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
}

export function useDataContext() {
  return useContext(DataContext);
}

// 导出清除缓存的工具函数
export const clearCache = (key) => cache.clear(key);
